import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { GridPageLayout } from '@lux/grid';
import { InlineAlert } from '@lux/inline-alert';
import { Tag, TitlePreset } from '@lux/title';
import { FlashMessage } from '@pegasus/ui-flashmessages';
import { Translate, TranslateScope } from '@pegasus/translation';
import { UpcomingProvider } from '@pegasus/upcoming-section';
import { BottomBubble } from '@browsing/components';
import { useConfig } from '@browsing/config';
import { useQuery } from '@browsing/hooks';
import { EarlyAccessPhase } from '@browsing/plus';

import { RECO_ITEM_WIDTH, TRACK_RECO_CAMPAIGN_OVERVIEW } from '@client/config/constants';
import { isXsScreen } from '@client/helpers/screens';
import { getCookie } from '@client/helpers/cookie';
import { RootState } from '@client/redux/types';
import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import { getCustomer, State as CustomerState } from '@client/redux/modules/customer';
import {
    getCategoryTabId,
    getOpenCampaigns,
    getTabs,
    makeGetUpcomingCampaigns,
    hasEarlyAccessCampaigns as getHasEarlyAccessCampaigns,
    getShowstoppers,
} from '@client/redux/modules/mylounge';
import { useRecommendations } from '@client/components/mylounge/hooks';
import { useTrackCampaignsImpression } from '@client/hooks/use-track-campaign-visibility';
import { incrementPageVisitsCount } from '@client/helpers';
import { useInitializeAndOpenBrazeSession, BrazeCard, COOKIE_BRAZE } from '@browsing/braze';

import { useFilterByFreshnessExperiment } from '@shared/abtests';
import { convertRecommendationsToLegacy } from '../shared/recommendations/helpers';
import { AccessibleHeader } from '../shared/accessible-header';
import Banner from './banner/banner';
import { CategoryTabIds } from './banner/constants';
import Grid from './campaigns/grid';
import MobileCampaigns from './campaigns/mobileCampaigns/mobile-campaigns';
import UpcomingSection from './campaigns/upcoming/upcoming-section-wrapper';
import { convertFlashMessageToAlertType } from './helpers';
import { CAMPAIGN_WRAPPER, UPCOMING_CAMPAIGNS_WRAPPER } from './constants';
import { CampaignExpiredStatus, ExpiredCampaignDialog } from './expired-campaign-dialog';
import RecommendationsWrapper from './recommendations-wrapper';
import { EarlyAccessSection, PlusWelcomeDialog, useZalandoPlus } from './plus';
import { CrossCampaignEntry } from './cross-campaign';
import { CampaignFilters } from './campaign-filters';

import * as S from './mylounge-wrapper.styles';
import { buildShowstopperContent } from './campaigns/showstopper';

type FlashMessageWithText = FlashMessage & {
    text: string;
};

interface Props {
    flashMessages?: FlashMessage[];
}

const EA_UPCOMING_SECTION = [EarlyAccessPhase.BuildUp, EarlyAccessPhase.PreEarlyAccess];

const EA_LIVE_SECTION = [EarlyAccessPhase.EarlyAccess];

const MyLoungeWrapper: FunctionComponent<Props> = ({ flashMessages }) => {
    const query = useQuery();
    const plus = useZalandoPlus();

    const recommendations = useRecommendations();
    const recommendationsLegacy = useMemo(
        () => convertRecommendationsToLegacy(recommendations),
        [recommendations],
    );

    useTrackCampaignsImpression();

    useEffect(() => {
        incrementPageVisitsCount('zl_mylounge_page_view_count');
    }, []);

    const categoryTabId = useSelector(getCategoryTabId);
    const tabs = useSelector(getTabs);
    const openCampaigns = useSelector(getOpenCampaigns);

    const getUpcomingCampaigns = useMemo(makeGetUpcomingCampaigns, []);
    const upcomingCampaigns = useSelector((state: RootState) =>
        getUpcomingCampaigns(state, plus.isSignupEnabled),
    );

    const hasEarlyAccessCampaigns = useSelector(getHasEarlyAccessCampaigns);
    const width = useSelector(getClientDeviceWidth);
    const xsScreen = isXsScreen(width);
    const config = useConfig();
    const customer = useSelector(getCustomer);
    const isPrive = config.isPriveDomain;
    const tabName = tabs?.find(t => t.id === categoryTabId)?.name;

    const [showExpiredCampaignDialog, setShowExpiredCampaignDialog] = useState(
        () => !!query?.status && !upcomingCampaigns.some(uc => uc.campaignId === query.event),
    );

    const expiredMessage =
        query?.status === 'upcoming'
            ? CampaignExpiredStatus.NOT_FOUND
            : CampaignExpiredStatus.EXPIRED;

    const handleExpiredDialogClose = useCallback(() => setShowExpiredCampaignDialog(false), []);

    const renderEarlyAccessShowstopper =
        plus.isEnabled && categoryTabId === CategoryTabIds.ALL && hasEarlyAccessCampaigns;

    const isCrossCampaignEnabled: boolean =
        config.isEnabled('isCrossCampaign') && categoryTabId === CategoryTabIds.ALL;

    const isFilterByFreshness: boolean = useFilterByFreshnessExperiment();

    const { hashedCustomerNumber, gender, firstName } = customer || {};

    const { isBrazeInitialised, areCardsReady, cardList, cardMethodsFromBraze } =
        useInitializeAndOpenBrazeSession(hashedCustomerNumber, gender, firstName);

    const isBrazeAllowedOnCookie = getCookie(COOKIE_BRAZE) === 'true';
    const canRenderBrazeCards =
        ((config.isEnabled('brazeCard') && typeof config.braze !== 'undefined') ||
            isBrazeAllowedOnCookie) &&
        isBrazeInitialised &&
        areCardsReady &&
        cardList?.length > 0;

    const showstoppers = useSelector(getShowstoppers);
    const showstopper =
        config.isEnabled('showstoppers') && showstoppers?.length > 0 ? showstoppers[0] : undefined;
    const showstopperContainer = useMemo(
        () => buildShowstopperContent(showstopper, openCampaigns),
        [showstopper, openCampaigns],
    );

    return (
        <>
            <TranslateScope path="myLounge">
                <Helmet>
                    <title>{tabName}</title>
                </Helmet>
                <S.OuterWrapper id="main">
                    <AccessibleHeader tag={Tag.H1}>
                        {isPrive ? 'ZALANDO PRIVÉ' : 'ZALANDO LOUNGE'}
                    </AccessibleHeader>
                    <ExpiredCampaignDialog
                        show={showExpiredCampaignDialog}
                        onClose={handleExpiredDialogClose}
                        showStatus={expiredMessage}
                    />
                    <Banner wrapperId={CAMPAIGN_WRAPPER} />
                    <AccessibleHeader tag={Tag.H2} aria-live="assertive">
                        {tabName || ''}
                    </AccessibleHeader>
                    {flashMessages && flashMessages.length !== 0 && (
                        <GridPageLayout>
                            <S.InlineFlashMessage>
                                {flashMessages.map((message: FlashMessageWithText) => (
                                    <InlineAlert
                                        key={message.messageId}
                                        type={convertFlashMessageToAlertType(message.type)}
                                    >
                                        {message.text || message.message}
                                    </InlineAlert>
                                ))}
                            </S.InlineFlashMessage>
                        </GridPageLayout>
                    )}
                    {canRenderBrazeCards && (
                        <GridPageLayout>
                            <BrazeCard
                                // @ts-expect-error
                                cards={cardList}
                                cardMethodsFromBraze={cardMethodsFromBraze}
                            />
                        </GridPageLayout>
                    )}
                    <UpcomingProvider>
                        {xsScreen && isCrossCampaignEnabled && <CrossCampaignEntry />}
                        {xsScreen &&
                            categoryTabId === CategoryTabIds.ALL &&
                            isFilterByFreshness && <CampaignFilters />}
                        {renderEarlyAccessShowstopper && (
                            <EarlyAccessSection renderWhenPhases={EA_LIVE_SECTION} />
                        )}
                        <GridPageLayout>
                            <S.Wrapper id={CAMPAIGN_WRAPPER}>
                                {xsScreen ? (
                                    <MobileCampaigns
                                        campaigns={openCampaigns}
                                        showstopper={showstopperContainer}
                                        isPlusEnabled={plus.isEnabled}
                                    />
                                ) : (
                                    <Grid
                                        width={width}
                                        campaigns={openCampaigns}
                                        isPlusEnabled={plus.isEnabled}
                                    />
                                )}
                                {!!recommendationsLegacy?.length && (
                                    <RecommendationsWrapper
                                        recommendations={recommendationsLegacy}
                                        width={width}
                                        itemWidth={RECO_ITEM_WIDTH}
                                        source={TRACK_RECO_CAMPAIGN_OVERVIEW}
                                    />
                                )}
                            </S.Wrapper>
                        </GridPageLayout>
                        <GridPageLayout>
                            <TranslateScope path="upcoming">
                                <S.UpcomingTitle preset={TitlePreset.S} tag={Tag.H2}>
                                    <Translate message="upcomingCampaigns.upcomingTitle" />
                                </S.UpcomingTitle>
                            </TranslateScope>
                        </GridPageLayout>
                        {renderEarlyAccessShowstopper && plus.isSignupEnabled && (
                            <EarlyAccessSection marginLess renderWhenPhases={EA_UPCOMING_SECTION} />
                        )}
                        <GridPageLayout>
                            <S.Wrapper $isLastContainer id={UPCOMING_CAMPAIGNS_WRAPPER}>
                                <UpcomingSection />
                            </S.Wrapper>
                        </GridPageLayout>
                    </UpcomingProvider>
                    <BottomBubble
                        customer={
                            customer as {
                                firstName: CustomerState['firstName'];
                                lastName: CustomerState['lastName'];
                                customerNumber: CustomerState['customerNumber'];
                                email: CustomerState['email'];
                            }
                        }
                    />
                </S.OuterWrapper>
            </TranslateScope>
            {plus.member && plus.isEnabled && !plus.isSignupEnabled && <PlusWelcomeDialog />}
        </>
    );
};

export default MyLoungeWrapper;
